import React, { useState, useRef, useEffect } from "react";
import { stripToDigits, stripToSignedInteger } from "../../ValidFunction";
import "./InputForm.scss";

const isDirtyField = (dirtyFields, name) => {
  if (!dirtyFields || !name) return false;
  if (dirtyFields[name]) return true;
  const parts = String(name)
    .replace(/\[(\d+)\]/g, ".$1")
    .split(".");
  let current = dirtyFields;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return false;
    current = current[part];
  }
  return Boolean(current);
};

const Input = ({
  label,
  name,
  register,
  type,
  errors,
  placeholder,
  onBlur = () => {},
  onFocus = () => {},
  onChange = () => {},
  disabled,
  readOnly,
  value,
  checked,
  dirtyFields,
  className,
  labelClassName = "p-title",
  toggleVisibility,
  showPassword,
  isPasswordField = false,
  digitsOnly = false,
  signedInteger = false,
}) => {
  const handleKeyPress = (e) => {
    if (signedInteger && !/[0-9-]/.test(e.key)) {
      e.preventDefault();
      return;
    }
    if (type === "tel" && !/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleInput = (e) => {
    if (digitsOnly) {
      e.target.value = stripToDigits(e.target.value);
    } else if (signedInteger) {
      e.target.value = stripToSignedInteger(e.target.value);
    }
  };

  const isCheckbox = type === "checkbox";

  return (
    <span>
      <label className={labelClassName} htmlFor={name}>
        {label}
      </label>
      <div className="input-wrapper" style={{ position: "relative" }}>
        <input
          className={`${className || ""} ${
            isDirtyField(dirtyFields, name) ? "dirtyFields" : ""
          }`.trim()}
          type={type}
          placeholder={placeholder}
          {...register(name, {
            onBlur: (e) => {
              onBlur(e);
            },
            onChange: (e) => {
              onChange(e);
            },
          })}
          id={name}
          disabled={disabled}
          readOnly={readOnly}
          {...(isCheckbox
            ? checked !== undefined
              ? { checked: Boolean(checked) }
              : {}
            : { value })}
          onKeyPress={handleKeyPress}
          onInput={handleInput}
          onFocus={onFocus}
          style={isPasswordField ? { paddingRight: "40px" } : {}}
        />
        {isPasswordField && (
          <span
            onClick={toggleVisibility}
            className="password-icon"
            style={{
              position: "absolute",
              right: "10px",
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            {showPassword ? (
              <i className="fa fa-eye-slash"></i>
            ) : (
              <i className="fa fa-eye"></i>
            )}
          </span>
        )}
      </div>
      {errors ? (
        <div className="text-danger">
          {errors[name] ? errors[name].message : null}
        </div>
      ) : null}
    </span>
  );
};

export default Input;

export const Select = React.forwardRef(
  (
    {
      name,
      label,
      options = [],
      errors,
      onChange,
      className,
      value,
      displayLabel,
      clearable = true,
      dirtyFields,
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedLabel, setSelectedLabel] = useState("");
    const [_selectedOption, setSelectedOption] = useState(null);
    const wrapperRef = useRef(null);
    const searchRef = useRef(null);
    const nativeRef = useRef(null);

    const setRefs = (el) => {
      nativeRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    };

    useEffect(() => {
      if (!value) {
        setSelectedLabel("");
        setSelectedOption(null);
        return;
      }

      if (!options.length) {
        if (displayLabel) {
          setSelectedLabel(displayLabel);
        }
        return;
      }

      const found = options.find(
        (o) => String(o.slug || o.Id || o.id) === String(value),
      );
      if (found) {
        setSelectedLabel(found.Name || found.name);
        setSelectedOption(found);
      } else if (displayLabel) {
        setSelectedLabel(displayLabel);
        setSelectedOption(null);
      }
    }, [value, options, displayLabel]);

    useEffect(() => {
      const handler = (e) => {
        if (wrapperRef.current && !wrapperRef.current.contains(e.target))
          setOpen(false);
      };
      document.addEventListener("mousedown", handler);
      return () => document.removeEventListener("mousedown", handler);
    }, []);

    useEffect(() => {
      if (open) searchRef.current?.focus();
    }, [open]);

    const filtered = options.filter((o) =>
      (o.Name || o.name || "").toLowerCase().includes(search.toLowerCase()),
    );

    const triggerNativeChange = (val) => {
      const el = nativeRef.current;
      if (!el) return;
      const nativeSetter = Object.getOwnPropertyDescriptor(
        window.HTMLSelectElement.prototype,
        "value",
      )?.set;
      if (nativeSetter) nativeSetter.call(el, val);
      else el.value = val;
      el.dispatchEvent(new Event("change", { bubbles: true }));
    };

    const handleSelect = (option) => {
      const val = option.slug || option.Id || option.id;
      const lbl = option.Name || option.name;
      setSelectedLabel(lbl);
      setSelectedOption(option);
      setOpen(false);
      setSearch("");
      triggerNativeChange(val);
      if (onChange) onChange({ target: { id: name, name, value: val } });
    };

    const handleClear = (e) => {
      e.stopPropagation();
      setSelectedLabel("");
      setSelectedOption(null);
      setSearch("");
      triggerNativeChange("");
      if (onChange) onChange({ target: { id: name, name, value: "" } });
    };

    const showClear = clearable && selectedLabel;
    const dirtyClass = isDirtyField(dirtyFields, name) ? "dirtyFields" : "";

    return (
      <div
        className={`ss-field ${className || ""} ${dirtyClass}`.trim()}
        ref={wrapperRef}
      >
        {label && <label className="ss-label">{label}</label>}

        <select
          ref={setRefs}
          name={name}
          id={name}
          style={{ display: "none" }}
          defaultValue=""
        >
          <option value="" />
          {options.map((o) => {
            const val = o.slug || o.Id || o.id;
            return (
              <option key={val} value={val}>
                {o.Name || o.name}
              </option>
            );
          })}
        </select>

        <button
          type="button"
          className={`ss-trigger ${open ? "ss-trigger--open" : ""} ${selectedLabel ? "ss-trigger--selected" : ""} ${dirtyClass}`.trim()}
          onClick={() => setOpen((p) => !p)}
        >
          <span className="ss-trigger-text">{selectedLabel || "בחר"}</span>
          <span className="ss-trigger-icons">
            {showClear && (
              <span className="ss-clear" onClick={handleClear} title="נקה">
                ✕
              </span>
            )}
            <span className="ss-arrow">{open ? "▲" : "▼"}</span>
          </span>
        </button>

        {open && (
          <div className="ss-dropdown">
            <div className="ss-search-row">
              <input
                ref={searchRef}
                type="text"
                className="ss-search"
                placeholder={`חיפוש${label ? " " + label : ""}...`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="ss-list">
              {filtered.length === 0 ? (
                <div className="ss-empty">לא נמצאו תוצאות</div>
              ) : (
                filtered.map((o) => {
                  const val = o.slug || o.Id || o.id;
                  const lbl = o.Name || o.name;
                  const isSelected = lbl === selectedLabel;
                  return (
                    <div
                      key={val}
                      className={`ss-option ${isSelected ? "ss-option--selected" : ""}`}
                      onClick={() => handleSelect(o)}
                    >
                      {isSelected && <span className="ss-option-check">✓</span>}
                      {lbl}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {errors?.[name] && (
          <span className="ss-error">{errors[name]?.message}</span>
        )}
      </div>
    );
  },
);
